/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 25/5/2023
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { generateOpenApiKey } from "../../Utils/keyGenerator";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

if (!API_BASE_URL) {
  // Without this, fetchBaseQuery falls back to relative URLs, every request
  // resolves against the app own origin and gets index.html back, and the
  // failure surfaces as an unexplained hang instead of a config error.
  console.error(
    "[pos-web] REACT_APP_BASE_URL is not set - this build cannot reach the API."
  );
}

// Endpoints that exist in order to OBTAIN a session, so they must go out
// anonymously. Defined in ../Services/AuthService.
const UNAUTHENTICATED_ENDPOINTS = new Set([
  "login",
  "register",
  "sendResetPWEmail",
  "validateOTP",
  "resetPassword",
]);

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    // A hung upstream should surface as an error instead of an endless spinner.
    timeout: 30000,
    prepareHeaders: (headers, { endpoint }) => {
      const token = sessionStorage.getItem("access_token");
      // Never attach a leftover token to a login/reset call: it makes the
      // backend authenticate a request that is meant to be anonymous.
      if (token && !UNAUTHENTICATED_ENDPOINTS.has(endpoint)) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      if (!headers.has("OpenApiKey")) {
        const key = generateOpenApiKey();
        headers.set("OpenApiKey", key);
      }
      return headers;
    },
  }),
  // setupListeners() is wired up in RootStore, so these actually fire. A POS
  // terminal sits on one screen for a long time; without them the cached
  // responses (table availability in particular) go stale and never refresh
  // until something happens to invalidate a tag.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  // Every tag used by a providesTags/invalidatesTags must be declared here.
  // "item" (ProductService) and "restaurantTickets" (CartService) were missing,
  // so RTK Query dropped them and the restaurant ticket list was never
  // invalidated after an item status change - it served the cached response.
  tagTypes: ["user", 'roles', "cat", "currency", "cart", "login", 'priv', 'brPriv', 'myOrders', 'table', 'customer', 'item', 'restaurantTickets', 'cards', 'image'],
  endpoints: (builder) => ({}),
});
